import { useEffect, useState } from "react";
import { db } from "../../firebase";

import {
  collection,
  getDocs,
  query,
  where,
  orderBy
} from "firebase/firestore";

import { useNavigate } from "react-router-dom";

import moment from "moment";

import VendorAssignmentModal from "../../components/purchase/VendorAssignmentModal";
import { cancelPurchaseRequest } from "../../services/purchaseRequestService";

// D-304 B: who may cancel a PR that has not become a PO.
const CAN_CANCEL_PR = ["owner", "sm", "manager"];

const isCancelled = (p) =>
  p.status === "cancelled" || p.procurement_status === "cancelled";

export default function PurchaseList() {

  const [data, setData] = useState([]);

  const navigate = useNavigate();

  const me   = JSON.parse(localStorage.getItem("user") || "{}");
  const role = String(me.role || "").toLowerCase().trim();

  const [
    showVendorModal,
    setShowVendorModal
  ] = useState(false);

  const [
    selectedPurchase,
    setSelectedPurchase
  ] = useState(null);

  const [
    expandedBatch,
    setExpandedBatch
  ] = useState(null);

  const [cancellingId, setCancellingId] = useState("");

  useEffect(() => {

    fetchData();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // FETCH PURCHASE REQUESTS
  // ============================================

  const fetchData = async () => {

    try {

      const companyId = me.company_id || "";

      const q = query(
        collection(db, "purchase_requests"),
        where("company_id", "==", companyId),
        orderBy("created_at", "desc")
      );

      const snap =
        await getDocs(q);

      const list =
        snap.docs.map((docSnap) => {

          const d =
            docSnap.data();

          const map = {};

          // Same item for the same site is shown once (old PRs had one row
          // per MRF line). D-304 B: a line WITHOUT an item_id (old custom
          // PR line) is no longer dropped — it keeps its own row, so it
          // reaches the PO.
          (d.items || []).forEach(
            (item, idx) => {

              const itemId =
                String(
                  item.item_id || ""
                ).trim();

              const siteId =
                String(
                  item.site_id ||
                  ""
                ).trim();

              const key = itemId
                ? `${itemId}_${siteId}`
                : `line_${idx}`;

              if (map[key]) {

                map[key].qty +=
                  Number(
                    item.qty || 0
                  );

                map[key].sources = [
                  ...(map[key].sources || []),
                  ...(item.sources || []),
                ];

              } else {

                map[key] = {

                  ...item,

                  qty: Number(
                    item.qty || 0
                  ),

                  site_id:
                    siteId,

                  sources:
                    Array.isArray(item.sources) ? item.sources : []
                };
              }
            }
          );

          return {

            id:
              docSnap.id,

            ...d,

            items:
              Object.values(map)
          };
        });

      setData(list);

    } catch (error) {

      console.error(
        "PURCHASE LIST ERROR:",
        error
      );
    }
  };

  // ============================================
  // UNIT — the line's OWN unit (D-304 B). It used to be looked up by item
  // NAME across all MRFs, which could show another item's unit.
  // ============================================

  const getUnit = (item) => {

    const raw =

      item.unit ||

      item.uom ||

      item.units ||

      item.unit_name ||

      "";

    if (!raw) {
      return "nos";
    }

    const u =
      raw.toLowerCase();

    if (
      ["meter", "mtr", "m"].includes(u)
    ) {

      return "meter";
    }

    if (
      ["kg", "kgs"].includes(u)
    ) {

      return "kg";
    }

    if (
      ["nos", "no", "number"].includes(u)
    ) {

      return "nos";
    }

    return raw;
  };

  // ============================================
  // TOGGLE EXPAND
  // ============================================

  const toggleBatch = (
    batchId
  ) => {

    setExpandedBatch(

      expandedBatch === batchId

        ? null

        : batchId
    );
  };

  // ============================================
  // OPEN VENDOR MODAL
  // ============================================

  const openVendorModal = (
    purchase
  ) => {

    setSelectedPurchase(
      purchase
    );

    setShowVendorModal(
      true
    );
  };

  // ============================================
  // CANCEL PR (D-304 B) — only before a PO exists. Its quantity stops
  // counting as "already ordered", so the need shows again in the
  // Procurement Queue.
  // ============================================

  const handleCancel = async (p) => {

    if (cancellingId) return;

    const reason = window.prompt(
      `Cancel ${p.purchase_request_no || "this purchase request"}?\n\nReason (mandatory):`
    );

    if (reason === null) return;

    if (!reason.trim()) {
      alert("Reason is mandatory.");
      return;
    }

    setCancellingId(p.id);

    try {

      await cancelPurchaseRequest({
        purchase_request_id: p.id,
        company_id:          me.company_id || "",
        reason,
        cancelled_by:        me.uid || me.id || "",
        cancelled_by_name:   me.name || me.email || "",
        cancelled_by_role:   role,
      });

      alert("Purchase request cancelled. Its items will show again in the Procurement Queue.");

      await fetchData();

    } catch (error) {

      console.error("CANCEL PR ERROR:", error);
      alert(error.message || "Cancel failed");

    } finally {

      setCancellingId("");
    }
  };

  const activeCount =
    data.filter(p => !isCancelled(p) && !p.po_id).length;

  // ============================================
  // UI
  // ============================================

  return (

    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-6">

      <div className="max-w-7xl mx-auto">

        {/* HEADER */}

        <div className="flex items-center justify-between mb-10">

          <div>

            <h1 className="text-4xl font-black tracking-tight text-slate-900">
              Purchase Requests
            </h1>

            <p className="text-slate-500 mt-2 text-sm">
              Procurement batches, vendor dispatch and GRN workflow.
            </p>

          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/purchase-orders")}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-semibold rounded-xl hover:bg-indigo-700 transition shadow-sm"
            >
              📋 Purchase Orders →
            </button>

            <div className="bg-white border border-slate-200 shadow-sm rounded-3xl px-8 py-5 min-w-[180px]">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-bold">
                Waiting for PO
              </div>
              <div className="text-4xl font-black text-slate-800 mt-1">
                {activeCount}
              </div>
            </div>
          </div>

        </div>

        {/* LIST */}

        {data.map((p) => {

          const cancelled = isCancelled(p);
          const canCancel =
            !cancelled &&
            !p.po_id &&
            CAN_CANCEL_PR.includes(role);

          return (

          <div
            key={p.id}
            className={`bg-white/90 backdrop-blur-sm rounded-3xl border border-slate-200/70 shadow-[0_10px_40px_rgba(15,23,42,0.06)] hover:shadow-[0_20px_60px_rgba(15,23,42,0.12)] transition-all duration-300 mb-4 overflow-hidden ${cancelled ? "opacity-60" : ""}`}
          >

            {/* HEADER */}

            <div

              onClick={() =>
                toggleBatch(p.id)
              }

              className="flex justify-between items-center px-5 py-4 border-b bg-gradient-to-r from-slate-50 to-white cursor-pointer hover:bg-slate-50 transition"
            >

              {/* LEFT */}

              <div>

                <div className="text-lg font-bold text-slate-800">

                  {
                    p.purchase_request_no ||

                    "Purchase Request"
                  }

                  {p.vendor_name && (
                    <span className="ml-2 text-sm font-semibold text-slate-500">
                      · {p.vendor_name}
                    </span>
                  )}

                </div>

                {/* DATE */}

                <div className="mt-1 text-sm text-gray-500">

                  Created:

                  {" "}

                  {
                    p.created_at?.toDate

                      ? moment(
                          p.created_at.toDate()
                        ).format(
                          "DD MMM YYYY • hh:mm A"
                        )

                      : "--"
                  }

                </div>

                {/* CREATED BY */}

                <div className="text-xs text-gray-400 mt-1">

                  Created By:

                  {" "}

                  {
                    p.created_by_name ||

                    "System"
                  }

                </div>

                {cancelled && (
                  <div className="text-xs text-red-600 mt-1">
                    Cancelled{p.cancelled_by_name ? ` by ${p.cancelled_by_name}` : ""}
                    {p.cancel_reason ? ` — ${p.cancel_reason}` : ""}
                  </div>
                )}

                {/* ANALYTICS */}

                <div className="flex flex-wrap items-center gap-2 mt-3">

                  <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-slate-100 text-slate-700">

                    {
  p.items?.filter(
    (item) => !item.deleted
  ).length || 0
}

                    {" "}
                    Items

                  </span>

                  <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-cyan-100 text-cyan-700">

                    {
                      p.total_sites || 0
                    }

                    {" "}
                    Sites

                  </span>

                  <span className="text-[11px] font-bold px-3 py-1 rounded-full bg-violet-100 text-violet-700">

                    {
                      p.total_mrfs || 0
                    }

                    {" "}
                    MRFs

                  </span>

                  <span className="text-[11px] text-cyan-600 font-semibold cursor-pointer">

                    {
                      expandedBatch === p.id

                        ? "Collapse"

                        : "Expand"
                    }

                  </span>

                </div>

              </div>

              {/* RIGHT */}

              <div className="flex items-center gap-3">

                {/* ========================================= */}
{/* PROCUREMENT ACTIONS */}
{/* ========================================= */}

{
  !cancelled &&
  (
  !p.procurement_status ||

  p.procurement_status ===
"pending_vendor"
) && !p.vendor_id && (

    <button

      onClick={(e) => {

        e.stopPropagation();

        openVendorModal(p);
      }}

      className="bg-gradient-to-r from-indigo-600 to-cyan-500 text-white text-xs px-4 py-2 rounded-xl shadow-md hover:scale-[1.02] transition"
    >

      Assign Vendor

    </button>
  )
}

{
  !cancelled &&
  !p.po_id &&
  (
    p.procurement_status === "vendor_assigned" ||
    (
      p.vendor_id &&
      (
        !p.procurement_status ||
        p.procurement_status === "pending_vendor"
      )
    )
  ) && (

    <button

      onClick={(e) => {

        e.stopPropagation();

        openVendorModal(p);
      }}

      className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-xs px-4 py-2 rounded-xl shadow-md hover:scale-[1.02] transition"
    >

      Generate PO

    </button>
  )
}

{
  canCancel && (

    <button

      onClick={(e) => {

        e.stopPropagation();

        handleCancel(p);
      }}

      disabled={cancellingId === p.id}

      className="bg-white border border-red-200 text-red-600 text-xs px-4 py-2 rounded-xl hover:bg-red-50 disabled:opacity-50"
    >

      {cancellingId === p.id ? "Cancelling..." : "Cancel PR"}

    </button>
  )
}

{
  p.procurement_status ===
    "po_generated" && (

    <button

      onClick={(e) => {

        e.stopPropagation();

        navigate(
          `/purchase-orders/${p.po_id}`
        );
      }}

      className="bg-gradient-to-r from-violet-600 to-indigo-500 text-white text-xs px-4 py-2 rounded-xl shadow-md"
    >

      View PO {p.po_number ? `(${p.po_number})` : ""}

    </button>
  )
}

{
  [
    "sent_to_vendor",
    "partial_delivery",
    "grn_complete"
  ].includes(
    p.procurement_status
  ) && (

    <button

      onClick={(e) => {

        e.stopPropagation();

        navigate(
          `/receive/${p.id}`
        );
      }}

      className="bg-gradient-to-r from-emerald-600 to-teal-500 text-white text-xs px-4 py-2 rounded-xl shadow-md"
    >

      GRN

    </button>
  )
}

                {/* STATUS */}

                <span
  className={`text-xs font-bold px-4 py-2 rounded-full border ${
    cancelled
      ? "bg-red-50 text-red-700 border-red-200"

      : (
  !p.procurement_status ||

  p.procurement_status ===
"pending_vendor"
)
      ? "bg-amber-100 text-amber-700 border-amber-200"

      : p.procurement_status ===
        "vendor_assigned"
      ? "bg-cyan-100 text-cyan-700 border-cyan-200"

      : p.procurement_status ===
  "po_generated"
? "bg-violet-100 text-violet-700 border-violet-200"

      : p.procurement_status ===
        "sent_to_vendor"
      ? "bg-blue-100 text-blue-700 border-blue-200"

      : p.procurement_status ===
        "partial_delivery"
      ? "bg-orange-100 text-orange-700 border-orange-200"

      : p.procurement_status ===
        "grn_complete"
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"

      : p.procurement_status ===
        "closed"
      ? "bg-slate-200 text-slate-700 border-slate-300"

      : "bg-gray-200 text-gray-600 border-gray-300"
  }`}
>

  {
    cancelled
      ? "Cancelled"

      : (
  !p.procurement_status ||

  p.procurement_status ===
"pending_vendor"
)
  ? (p.vendor_id ? "Waiting for PO" : "Pending Vendor Assignment")

      : p.procurement_status ===
        "vendor_assigned"
      ? "Vendor Assigned"

      : p.procurement_status ===
  "po_generated"
      ? "PO Generated"

      : p.procurement_status ===
        "sent_to_vendor"
      ? "Sent To Vendor"

      : p.procurement_status ===
        "partial_delivery"
      ? "Partial Delivery"

      : p.procurement_status ===
        "grn_complete"
      ? "GRN Complete"

      : p.procurement_status ===
        "closed"
      ? "Closed"

      : p.procurement_status
  }

</span>

              </div>

            </div>

            {/* EXPANDED ITEMS */}

            {
              expandedBatch === p.id && (

                <div className="divide-y">

                  {
                    p.items?.map(
                      (item, i) => {

                        const unit =
                          getUnit(item);

                        return (

                          <div
                            key={`${item.item_id || "line"}_${item.site_id}_${i}`}
                            className="flex justify-between items-center px-6 py-3 hover:bg-slate-50/70 transition"
                          >

                            <div>

                              <div className="text-sm font-semibold text-slate-700">
                                {item.item_name}
                              </div>

                              <div className="text-xs text-gray-400">
                                {item.item_code || (item.item_id ? item.item_id : "Custom item")}
                                {item.specification ? ` · ${item.specification}` : ""}
                              </div>

                              {(item.sources || []).length > 0 && (
                                <div className="text-[11px] text-gray-400 mt-0.5">
                                  For: {item.sources.map(s => `${s.site_name || s.mrf_no || "MRF"} (${s.qty})`).join(", ")}
                                </div>
                              )}

                            </div>

                            <div className="text-sm font-semibold">

                              {item.qty} {unit}

                            </div>

                          </div>
                        );
                      }
                    )
                  }

                </div>
              )
            }

          </div>
          );
        })}

        {
          data.length === 0 && (

            <div className="text-center py-10 text-gray-400">

              No purchase requests found

            </div>
          )
        }

      </div>

      {/* MODAL */}

      <VendorAssignmentModal

        open={showVendorModal}

        onClose={() => {

  setShowVendorModal(false);

  setSelectedPurchase(null);

  fetchData();
}}

        purchaseRequest={selectedPurchase}

      />

    </div>
  );
}